/**
 * Your own character's name, coloured — and nobody else's.
 *
 * Every beat is narrated in the third person for the whole table, because
 * second person addresses everyone at once: three players read one character's
 * search as their own. That leaves one question the prose cannot answer at a
 * glance — *which of these names is me* — and it is the interface's to answer,
 * not the grammar's.
 *
 * **Each client only knows its own name**, so a beat lights up for exactly one
 * reader and stays plain for everybody else. No per-recipient text, no second
 * model call, and nothing to diverge between what was sent and what was stored.
 */

/** Split on the name, keeping it, so the pieces can be styled alternately. */
function pieces(text: string, name: string): string[] {
  // A name is arbitrary text a player typed, so it is escaped before it goes
  // anywhere near a pattern — "Bex (the Younger)" is a legal name and an
  // illegal expression.
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return text.split(new RegExp(`(${escaped})`, 'gi'));
}

export function Mention({
  text,
  name,
}: {
  readonly text: string;
  readonly name: string | null | undefined;
}) {
  // Nothing to mark before the table has said who you are, and nothing to do
  // for a beat your name does not appear in.
  if (!name || !text.toLowerCase().includes(name.toLowerCase())) return <>{text}</>;

  return (
    <>
      {pieces(text, name).map((piece, index) =>
        // The odd pieces are the matches: `split` with a capturing group puts
        // them between the parts it split on.
        index % 2 === 1 ? (
          // `amber-700`, not `600`: against the light theme's white the 600 is
          // 3.19:1 where AA wants 4.5, which axe reports as serious. It went
          // unseen because it only renders when a beat contains the viewer's
          // own name — rare until the objection announcements, which name the
          // appellant every time.
          <strong key={index} className="font-semibold text-amber-700 dark:text-amber-400">
            {piece}
          </strong>
        ) : (
          <span key={index}>{piece}</span>
        ),
      )}
    </>
  );
}
